import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';

const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const ProfilePage = lazy(() => import('./pages/Profile').then(m => ({ default: m.ProfilePage })));
const AttendancePage = lazy(() => import('./pages/Attendance').then(m => ({ default: m.AttendancePage })));
const LeavePage = lazy(() => import('./pages/Leave').then(m => ({ default: m.LeavePage })));
const PayrollPage = lazy(() => import('./pages/Payroll').then(m => ({ default: m.PayrollPage })));
const EmployeesPage = lazy(() => import('./pages/Employees').then(m => ({ default: m.EmployeesPage })));
const AssetManagementPage = lazy(() => import('./pages/AssetManagement').then(m => ({ default: m.AssetManagementPage })));
const ReimbursementPage = lazy(() => import('./pages/Reimbursement').then(m => ({ default: m.ReimbursementPage })));
const ReportsPage = lazy(() => import('./pages/Reports').then(m => ({ default: m.ReportsPage })));
const SettingsPage = lazy(() => import('./pages/Settings').then(m => ({ default: m.SettingsPage })));
const PendingRegistrationsPage = lazy(() => import('./pages/PendingRegistrations').then(m => ({ default: m.PendingRegistrationsPage })));
const AttendanceOverviewPage = lazy(() => import('./pages/AttendanceOverview').then(m => ({ default: m.AttendanceOverviewPage })));

function PageFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div className="spinner w-8 h-8 border-4 border-primary-600 border-t-transparent" />
    </div>
  );
}

function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="spinner w-10 h-10 border-4 border-primary-600 border-t-transparent" />
          <p className="text-gray-600 font-medium">Loading South Asia Consultancy...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Suspense fallback={<PageFallback />}><Login /></Suspense>}
      />
      <Route
        path="/register"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Suspense fallback={<PageFallback />}><Register /></Suspense>}
      />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Suspense fallback={<PageFallback />}><Dashboard /></Suspense>} />
        <Route path="/profile" element={<Suspense fallback={<PageFallback />}><ProfilePage /></Suspense>} />
        <Route path="/attendance" element={<Suspense fallback={<PageFallback />}><AttendancePage /></Suspense>} />
        <Route path="/attendance-overview" element={<Suspense fallback={<PageFallback />}><AttendanceOverviewPage /></Suspense>} />
        <Route path="/leave" element={<Suspense fallback={<PageFallback />}><LeavePage /></Suspense>} />
        <Route path="/payroll" element={<Suspense fallback={<PageFallback />}><PayrollPage /></Suspense>} />
        <Route path="/employees" element={<Suspense fallback={<PageFallback />}><EmployeesPage /></Suspense>} />
        <Route path="/pending-registrations" element={<Suspense fallback={<PageFallback />}><PendingRegistrationsPage /></Suspense>} />
        <Route path="/assets" element={<Suspense fallback={<PageFallback />}><AssetManagementPage /></Suspense>} />
        <Route path="/reimbursement" element={<Suspense fallback={<PageFallback />}><ReimbursementPage /></Suspense>} />
        <Route path="/reports" element={<Suspense fallback={<PageFallback />}><ReportsPage /></Suspense>} />
        <Route path="/settings" element={<Suspense fallback={<PageFallback />}><SettingsPage /></Suspense>} />
      </Route>

      {/* Redirect root to dashboard or login */}
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
      />

      {/* 404 - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default App;
